import { controllerReason, failureReason, FAILURE_TEXT } from './reasons.js';
import { categorize } from './miss_category.js';
import { coverageOf, dialable, isPlaceholderCarrier, sideOf } from './side.js';
import { pacificClock, pacificDay } from './time.js';
import { policeState } from './police.js';
import { expectedWait } from './agency.js';
// Neila's CA agency list (2026-10-13): the agency's expected wait on the waiting line, when known
const waitOf = (m, tasks) => { const w = expectedWait(m, tasks); return w ? `; ${w}` : ''; };
/** SJ Automations, the user every automation (Ins Creation, the Claims Agent write-back, the controller) writes as. */
export const AUTOMATION_USER = '005Ql00000BTfB8IAL';
const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const ms = (s) => (s ? Date.parse(s.replace(/\+0000$/, 'Z')) : NaN);
const NO_OWN_POLICY_CASES = /\b(bicycle|pedestrian|electric scooter|e-?scooter)\b/i;
const STATE_FARM = /\bstate farm\b/i;
function isWin(c) {
    return c.Disposition__c === 'Success' || !blank(c.Claim_Number__c);
}
/** What it takes to open a "Needs you" row, in the claims team's words. */
export const NEED = {
    live: (r) => `A person to call ${r.carrier ?? 'the carrier'}: they only take this claim from a person`,
    note: () => 'Copy the claim number from the note onto the record',
    nopolicy: (r) => (r.side === 'PLT' ? "The policy number (client's insurance card)"
        : r.side === 'DEF' || r.side === 'Other Party' ? "The at-fault driver's policy # or name (police report or exchange of info)" : 'The policy number or the insured name'),
    aifail: () => "The right policy number (the carrier couldn't find this one)",
    missing: () => 'The fact the carrier asked for, then send the AI again',
    elsewhere: () => 'File it where the carrier said (online, email or another number)',
    handoff: () => 'Check whether our team opened it after the AI handed the call over',
    nocarrier: (r) => (r.side === 'DEF' ? "The at-fault driver's insurer (police report or client)" : 'The right carrier for this record'),
    nophone: (r) => `${r.carrier ?? "The carrier"}'s claims phone number`,
    nocall: () => 'Send the AI (no call was made)',
    placeholder: () => 'The real claim number (the field holds a placeholder)',
    cap: () => 'A person to call (the AI called 3 times this week)',
    review: () => 'Someone to read the last call summary and decide',
    tcr: (r) => `Add ${r.tcrCarrier ?? 'the carrier'} from the police report as the DEF`,
    no3p: () => 'Confirm no insurance (police report and DMV SR19), then UM',
    gap: () => 'A person to look at it',
    police: () => 'Start the police report request, or get the at-fault insurer from the client',
    companion: () => "The claim number for this client (normally the main client's claim number)",
    companion_carrier: (r) => `This record's own claim number, if ${r.carrier ?? 'the carrier'} opened one`,
    companion_main: () => "Add the record in Salesforce from the main client's case",
    other: () => 'A person to look at it',
};
/** Whether texting our own client is the right ask for this row: only for the client's own policy (PLT). The other driver's policy # or
 * name (DEF, Other Party) comes from the police report or the exchange of information, a different ask (coordinator 10-07). */
export function asksOurClient(r) {
    return r.side === 'PLT';
}
/** The call review's sentence without its reason code ("POLICY_NOT_FOUND: ..."); null when there is none. */
export function callNote(missReason) {
    const s = String(missReason ?? '').trim().replace(/^[A-Z][A-Z_]+:\s*/, '').trim();
    if (!s || /^\((?:compose failed|no detail returned)\)$/.test(s))
        return null;
    return s;
}
export function callResult(c) {
    if (isWin(c))
        return 'got a claim number';
    if (blank(c.Disposition__c))
        return 'no result yet';
    const cat = categorize(c);
    return cat.key === 'OTHER' || cat.group === 'NOT_DIALED' || cat.key === 'UNKNOWN' ? cat.text : FAILURE_TEXT[failureReason(c)];
}
function base(row) {
    const carrier = row.litify_ext__Insurance_Company_Party__r?.Name ?? null;
    return {
        insuranceId: row.Id,
        side: sideOf(row),
        coverage: coverageOf(row),
        carrier: carrier && !isPlaceholderCarrier(carrier) ? carrier : null,
        policy: blank(row.litify_pm__Policy_Number__c) ? null : String(row.litify_pm__Policy_Number__c),
        claim: blank(row.litify_pm__Claim_Number__c) ? null : String(row.litify_pm__Claim_Number__c),
        openedBy: null,
        openedByName: null,
        openedAt: null,
        nextAt: null,
        waitingSince: row.CreatedDate,
        lor: null,
        calls: 0,
        lastCall: null,
        need: null,
        who: null,
    };
}
const LOR_TEXT = {
    DELIVERED: 'LOR delivered', EXPECTED: 'LOR due', SENT_PENDING: 'LOR sent, confirming', BOUNCED: 'LOR bounced', FAX_FAILED: 'LOR fax failed',
    ASK: 'LOR not sent', NOT_DUE: '', UNKNOWN: '',
};
/** One insurance row to open: spec section 2, first match wins; then what it takes and who has the next move. */
export function rowStatus(ctx) {
    const r = rowStatusCore(ctx);
    if (r.status === 'need')
        return { ...r, need: NEED[r.fix ?? 'other'](r), who: 'team' };
    // an opened record whose letter the controller could not send (lor_rows ASK): it joins the issues list (2026-10-09: 151 sat as a small label)
    if (r.status === 'opened' && ctx.lor?.state === 'ASK') {
        return { ...r, who: 'team', lorAsk: true, need: ctx.lor.reason === 'LOR_ALL_FAILED' ? 'Send the LOR: every automatic send failed' : 'Send the LOR to the carrier: the system could not send it' };
    }
    if (r.status === 'working')
        return { ...r, who: r.who ?? 'system' };
    return { ...r, who: null };
}
function rowStatusCore(ctx) {
    const { row, matter, calls, history, ledger, users, now } = ctx;
    const b = base(row);
    // a letter a person recorded as already on file (controller lor_rows DELIVERED / LOR_ON_FILE, 2026-10-19) never reads "delivered"
    if (ctx.lor)
        b.lor = ctx.lor.state === 'DELIVERED' && ctx.lor.reason === 'LOR_ON_FILE' ? 'LOR on file' : LOR_TEXT[ctx.lor.state] || null;
    const latestFirst = calls.slice().sort((x, y) => ms(y.CreatedDate) - ms(x.CreatedDate));
    const last = latestFirst[0];
    b.calls = calls.length;
    b.lastCall = last ? { at: last.CreatedDate, result: callResult(last), note: callNote(last.Miss_Reason__c) } : null;
    const waitFrom = Math.max(ms(row.CreatedDate), ms(matter.CreatedDate));
    b.waitingSince = new Date(Number.isFinite(waitFrom) ? waitFrom : ms(row.CreatedDate)).toISOString();
    const out = (status, text, fix = null, who = null) => ({ ...b, status, text, fix: status === 'need' ? (fix ?? 'other') : null, who });
    // 1. a claim number on the record
    if (b.claim) {
        const first = history.filter((h) => blank(h.OldValue) && !blank(h.NewValue)).sort((x, y) => ms(x.CreatedDate) - ms(y.CreatedDate))[0];
        const at = first ? first.CreatedDate : row.CreatedDate;
        const aiWin = calls.some((c) => isWin(c) && ms(c.CreatedDate) <= ms(at) + 60_000);
        let openedBy;
        let who = null;
        if (aiWin)
            openedBy = 'AI';
        else if (!first && row.CreatedById === AUTOMATION_USER)
            openedBy = 'intake';
        else {
            const by = first ? first.CreatedById : row.CreatedById;
            if (by === AUTOMATION_USER)
                openedBy = 'AI';
            else {
                openedBy = 'team';
                who = by ? users[by] ?? null : null;
            }
        }
        const byText = openedBy === 'AI' ? 'by the AI' : openedBy === 'intake' ? 'from the intake' : who ? `by ${who}` : 'by our team';
        return { ...b, status: 'opened', text: `Opened ${byText} at ${pacificClock(at)}`, fix: null, openedBy, openedByName: who, openedAt: at };
    }
    // 2. worker's comp
    if (b.side === 'WC')
        return out('none', "Worker's comp is handled outside this console");
    // 3. the controller ledger decides when it tracks the row
    if (ledger) {
        const p = controllerReason(ledger.decision, ledger.reason);
        if (p.status !== 'opened') {
            const r = out(p.status, p.text, p.fix, p.who ?? null);
            r.nextAt = ledger.next_attempt_at ?? null;
            if (ledger.first_failure_at)
                r.waitingSince = ledger.first_failure_at;
            if (r.status === 'working' && r.nextAt && ms(r.nextAt) > now.getTime())
                r.text = `${r.text}. Next try ${pacificClock(r.nextAt)}`;
            return r;
        }
    }
    const carrierName = row.litify_ext__Insurance_Company_Party__r?.Name ?? null;
    // 4. an AI call running now
    if (last && blank(last.Disposition__c) && now.getTime() - ms(last.CreatedDate) < 3_600_000) {
        return out('working', `AI calling ${b.carrier ?? 'the carrier'} now (started ${pacificClock(last.CreatedDate)})`);
    }
    // 5. no real carrier
    if (!b.carrier) {
        const caseType = matter.litify_pm__Case_Type__r?.Name ?? '';
        if (b.side === 'PLT' && NO_OWN_POLICY_CASES.test(caseType) && !b.policy) {
            return out('none', `No own auto policy on file (${caseType.toLowerCase()} case); it won't always exist`);
        }
        if (b.side === 'DEF' && ctx.police) {
            const p = policeState(matter, ctx.police.docs, ctx.police.tasks);
            switch (p.kind) {
                case 'namesCarrier': return { ...out('need', `The police report names ${p.carrier} as the other driver's insurer; add it as the at-fault (DEF) insurance`, 'tcr'), tcrCarrier: p.carrier };
                // California (Neila 2026-10-13): HDM runs the DMV SR19, then the client or a Coastal carrier search
                case 'noCarrier': return /^CA\b/i.test(matter.litify_pm__Matter_State__c ?? '')
                    ? out('need', 'The police report lists no insurance for the other driver; HDM runs the DMV SR19, then ask the client or run a Coastal carrier search', 'no3p')
                    : out('need', p.saysNone ? 'The police report says the other driver had no insurance; the DMV SR19 confirms it' : 'The police report is in but names no at-fault insurer', 'no3p');
                case 'noReportFiled': return out('need', `No police report was filed (${p.what}); get the other driver's insurer from the exchange of information or the client`, 'nocarrier');
                case 'requested': return p.notStarted
                    ? out('need', `The police report request was never started (task created ${pacificDay(p.at)}, still Not Started); no report is on its way`, 'police')
                    : out('working', `Waiting on the police report (requested ${pacificDay(p.at)}${waitOf(matter, ctx.police.tasks)})`, null, 'police');
                case 'requestedClosed': return out('need', `The police report request was closed ${pacificDay(p.at)}, but no report is in our records; check Docrio`, 'review');
                case 'notRequested': return out('need', 'No police report requested yet, and no at-fault insurer on file', 'nocarrier');
            }
        }
        return out('need', b.side === 'DEF' ? 'No at-fault insurer on file yet' : 'No carrier on file for this record', 'nocarrier');
    }
    // 6. nothing the AI can call with (a second client's record on the main client's carrier follows the main claim instead: board.ts
    // companionRow, Codex 10-14 #13: only then, never for another carrier)
    if (!b.policy && blank(row.Insured__c)) {
        return out('need', "No policy number or insured name, so the AI can't call", 'nopolicy');
    }
    // 7. second client on a companion case
    if (!last && matter.Lead_Matter__c === false && matter.litify_pm__Companion__c) {
        return out('working', "Second client from the same crash; follows the first client's claim");
    }
    // 8. State Farm: website only
    if (STATE_FARM.test(carrierName ?? ''))
        return out('working', 'State Farm takes claims on its website; the website lane has it');
    // 9. the last failed call decides
    if (last) {
        const k = failureReason(last);
        const at = pacificClock(last.CreatedDate);
        b.waitingSince = last.CreatedDate;
        // Cesar 10-09: say the real stopping reason; a request stopped before dialing is not "the AI called"
        const cat = categorize(last);
        if (cat.group === 'NOT_DIALED')
            return { ...out('need', `Not called (${at}): ${cat.text}`, 'review'), waitingSince: last.CreatedDate };
        if (cat.key === 'OTHER' || cat.key === 'UNKNOWN')
            return { ...out('need', `The AI called at ${at}: ${cat.text}`, 'review'), waitingSince: last.CreatedDate };
        if (k === 'noPerson')
            return out('working', `The AI called at ${at} and ${FAILURE_TEXT[k]}; the system calls again`);
        const fix = k === 'notFound' ? 'aifail' : k === 'missing' ? 'missing' : k === 'ai' ? 'live' : k === 'elsewhere' ? 'elsewhere' : k === 'handedOff' ? 'handoff' : 'review';
        return { ...out('need', `The AI called at ${at} and ${FAILURE_TEXT[k]}`, fix), waitingSince: last.CreatedDate };
    }
    // no dialable phone and no call
    const phone = row.litify_ext__Insurance_Company_Party__r?.Phone ?? null;
    if (!dialable(phone) && now.getTime() - ms(matter.CreatedDate) >= 7_200_000) {
        return out('need', `${b.carrier} has no phone number on file, so the AI can't call`, 'nophone');
    }
    // 10. no call yet
    if (now.getTime() - ms(matter.CreatedDate) < 7_200_000)
        return out('working', 'AI call queued for this new matter');
    return out('need', 'No AI call was made on this record', 'nocall');
}
//# sourceMappingURL=status.js.map